import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, X, ListChecks, CalendarClock, FlaskConical, Shuffle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ExperimentSetup, startBody } from "@/v2/Growth";

const due = (p) => (p.due_date ? p.due_label || new Date(`${p.due_date}T12:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "");

function ExperimentOffer({ o, msgId, onAdded, onSkip }) {
  const [open, setOpen] = useState(false);
  const pick = { id: "custom", ...o.experiment, custom: o.experiment };
  const start = async (p, days, baseline) => {
    try { await api.post("/v2/experiments", { ...startBody(p, days, baseline), msg_id: msgId, offer_id: o.id }); toast.success("Experiment started. Log it each evening on Today."); onAdded(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-[#DCE6DE] bg-[#EDF2EE] px-3 py-2 text-sm" data-testid={`offer-${o.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FlaskConical className="h-4 w-4 text-[#4F6B55]" />
        <span className="flex-1 min-w-[12rem]">{o.status === "added" ? "Experiment started: " : "Test it on yourself: "}<b>{o.experiment.title}</b></span>
        {o.status === "added" ? <Check className="h-4 w-4 text-[#6E8B74]" data-testid={`offer-added-${o.id}`} /> : !open && (
          <span className="flex gap-1">
            <button onClick={() => setOpen(true)} data-testid={`offer-exp-open-${o.id}`} className="h-11 px-4 rounded-full bg-[#4F6B55] text-white text-xs font-semibold">Set it up</button>
            <button onClick={onSkip} data-testid={`offer-dismiss-${o.id}`} className="h-11 px-4 rounded-full border border-stone-300 text-xs font-semibold">Not now</button>
          </span>)}
      </div>
      {open && o.status !== "added" && <ExperimentSetup pick={pick} onStart={start} onBack={() => setOpen(false)} />}
    </div>
  );
}

function AdjustOffer({ o, msgId, onAdded, onSkip }) {
  const [busy, setBusy] = useState(false);
  const change = async () => {
    setBusy(true);
    try { await api.post(`/v2/today/must/${encodeURIComponent(o.must_id)}/adjust`, { text: o.text, scope: o.scope, msg_id: msgId, offer_id: o.id, date: o.date || null, from_text: o.from || null }); toast.success(o.scope === "goal" ? "Changed, and it's your new default." : "Changed on today's must-dos."); onAdded(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const done = o.status === "added";
  return (
    <div className="rounded-2xl border border-[#F1E6CF] bg-[#FAF4E8] px-3 py-2 text-sm" data-testid={`offer-${o.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Shuffle className="h-4 w-4 text-[#8A5A16]" />
        <span className="flex-1 min-w-[12rem]">{done ? "Changed to: " : o.scope === "goal" ? `Make this the new default${o.goal ? ` for "${o.goal}"` : ""}: ` : "Change today's must-do to: "}<b>{o.text}</b>
          {!done && <span className="block text-xs text-stone-500">Instead of: {o.from}</span>}</span>
        {done ? <Check className="h-4 w-4 text-[#6E8B74]" data-testid={`offer-added-${o.id}`} /> : (
          <span className="flex gap-1">
            <button disabled={busy} onClick={change} data-testid={`offer-adjust-${o.id}`} className="h-11 px-4 rounded-full bg-stone-900 text-white text-xs font-semibold disabled:opacity-50">Change it</button>
            <button onClick={onSkip} data-testid={`offer-dismiss-${o.id}`} className="h-11 px-4 rounded-full border border-stone-300 text-xs font-semibold">Keep it</button>
          </span>)}
      </div>
    </div>
  );
}

export function OfferChips({ offers, msgId, onChange }) {
  const set = (id, status) => onChange(offers.map((o) => (o.id === id ? { ...o, status } : o)));
  const [saving, setSaving] = useState(null);
  const add = async (o) => {
    if (saving) return;
    setSaving(o.id);
    try {
      await api.post("/v2/plans", { text: o.text, kind: o.kind, due_date: o.due_date, due_label: o.due_label, msg_id: msgId, offer_id: o.id });
      set(o.id, "added");
      toast.success("Kept in My plans", { id: `kept-${o.id}` });
    } catch (e) { toast.error(`Not saved: ${errMsg(e)}. Tap Keep this idea to try again.`); } finally { setSaving(null); }
  };
  const skip = (o) => { set(o.id, "dismissed"); if (msgId) api.post("/v2/plans/offer/dismiss", { msg_id: msgId, offer_id: o.id }).catch((e) => console.error(e)); };
  const visible = offers.filter((o) => o.status !== "dismissed");
  return (
    <div className="mt-2 space-y-2" data-testid="offer-chips">
      {visible.map((o) => o.kind === "adjust" ? (
        <AdjustOffer key={o.id} o={o} msgId={msgId} onAdded={() => set(o.id, "added")} onSkip={() => skip(o)} />
      ) : o.kind === "experiment" && o.experiment ? (
        <ExperimentOffer key={o.id} o={o} msgId={msgId} onAdded={() => set(o.id, "added")} onSkip={() => skip(o)} />
      ) : (
        <div key={o.id} className="flex flex-wrap items-center gap-2 rounded-2xl border border-stone-200 bg-[#F5F2EC] px-3 py-2 text-sm" data-testid={`offer-${o.id}`}>
          <ListChecks className="h-4 w-4 text-stone-500" />
          <span className="flex-1 min-w-0">{o.status === "added" ? "Kept in My plans: " : "Idea: "}<b>{o.text}</b>{o.due_date && <span className="text-stone-500"> · {due(o)}</span>}</span>
          {o.status === "added" ? <Check className="h-4 w-4 text-[#6E8B74]" data-testid={`offer-added-${o.id}`} /> : (
            <span className="flex gap-1">
              <button onClick={() => add(o)} disabled={saving === o.id} data-testid={`offer-add-${o.id}`} className="h-11 px-4 rounded-full bg-[#392B43] text-white text-xs font-semibold disabled:opacity-60">{saving === o.id ? "Saving…" : "Keep this idea"}</button>
              <button onClick={() => skip(o)} data-testid={`offer-dismiss-${o.id}`} className="h-11 px-4 rounded-full border border-stone-300 text-xs font-semibold">Not now</button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export async function makeTodayStep(plan, onDone) {
  try {
    const { data } = await api.post("/v2/today/plan-step", { plan_id: plan.id });
    toast.success(data.pending ? "Saved. It'll be your step as soon as you do today's energy check." : "Today's step: your plan. CAMRYN's suggestion will wait.");
    onDone();
  } catch (e) { toast.error(errMsg(e)); }
}

function TodayButton({ plan, today, onDone }) {
  if (!today || today.step_done) return null;
  if (today.plan_id === plan.id) return <span className="text-xs font-semibold text-[#6E8B74]" data-testid={`plan-is-today-${plan.id}`}>{today.pending ? "Your step after the energy check" : "Today's step"}</span>;
  const taken = !!today.plan_id;
  return (
    <button disabled={taken} onClick={() => makeTodayStep(plan, onDone)} data-testid={`plan-make-today-${plan.id}`}
      className="tap text-xs font-semibold text-[#B5654A] disabled:text-stone-400 disabled:cursor-not-allowed text-left">
      {taken ? `Already today's step: ${today.plan_text || "another plan"}` : "Make it today's step"}
    </button>
  );
}

export function FollowUpCard({ plan, today, onDone }) {
  const act = async (action, msg) => {
    try { await api.post(`/v2/plans/${plan.id}`, { action }); toast.success(msg); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card p-5 rise" data-testid="followup-card">
      <span className="eyebrow flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />You planned{plan.due_label ? ` · ${plan.due_label}` : ""}</span>
      <p className="font-display text-xl mt-2 leading-snug" data-testid="followup-text">{plan.text}</p>
      <p className="text-sm text-stone-600 mt-1">Did it happen? No pressure either way.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => act("done", "Nice. Ticked off.")} data-testid="followup-done" className="h-11 px-5 rounded-full bg-stone-900 text-white text-sm font-semibold">Done</button>
        <button onClick={() => act("later", "Moved to a couple of days from now.")} data-testid="followup-later" className="h-11 px-5 rounded-full border border-stone-300 text-sm font-semibold">Not yet</button>
        <button onClick={() => act("drop", "Let go. It won't come up again.")} data-testid="followup-drop" className="h-11 px-5 rounded-full text-sm text-stone-600 underline">Let it go</button>
      </div>
      <div className="mt-3"><TodayButton plan={plan} today={today} onDone={onDone} /></div>
    </div>
  );
}

function PromoteButton({ plan, onPromote }) {
  return <button onClick={() => onPromote(plan)} data-testid={`plan-promote-${plan.id}`} className="tap text-xs font-semibold text-[#B5654A] text-left">Add to today's must-dos</button>;
}

function PlanRow({ p, onAct, today, onToday, onPromote, inToday }) {
  return (
    <li className="flex items-center gap-3 py-2" data-testid={`plan-item-${p.id}`}>
      <button onClick={() => onAct(p, p.status === "done" ? "reopen" : "done")} aria-label="Mark done" data-testid={`plan-done-${p.id}`}
        className={`h-7 w-7 shrink-0 rounded-full border grid place-items-center tap ${p.status === "done" ? "bg-[#6E8B74] border-[#6E8B74] text-white" : "border-stone-300"}`}>
        {p.status === "done" && <Check className="h-4 w-4" />}
      </button>
      <span className="flex-1 min-w-0 text-sm"><span className={p.status === "done" ? "line-through text-stone-400" : ""}>{p.text}</span>
        {p.due_date && p.status !== "done" && <span className={`ml-2 text-xs ${p.due_now ? "text-[#B5654A] font-semibold" : "text-stone-500"}`}>{due(p)}</span>}
        {p.status === "open" && inToday?.includes(p.id) && <span className="block mt-0.5 text-xs font-semibold text-[#6E8B74]" data-testid={`plan-in-today-${p.id}`}>On today's must-dos</span>}
        {p.status === "open" && onPromote && !inToday?.includes(p.id) && <span className="block mt-0.5"><PromoteButton plan={p} onPromote={onPromote} /></span>}
        {p.status === "open" && onPromote === undefined && <span className="block mt-0.5"><TodayButton plan={p} today={today} onDone={onToday} /></span>}</span>
      {p.status !== "done" && <button onClick={() => onAct(p, "drop")} aria-label="Let it go" data-testid={`plan-drop-${p.id}`} className="tap h-8 w-8 grid place-items-center text-stone-400 hover:text-stone-700"><X className="h-4 w-4" /></button>}
    </li>
  );
}

export function PlansCard({ refresh, onToday, onPromote, inToday, title = "My plans" }) {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [all, setAll] = useState(false);
  const load = () => api.get("/v2/plans").then(({ data }) => setD(data)).catch((e) => console.error(e));
  useEffect(() => { load(); }, [refresh]);
  const act = async (p, action) => { try { await api.post(`/v2/plans/${p.id}`, { action }); load(); if (d.today?.plan_id === p.id || onPromote !== undefined) onToday?.(); } catch (e) { toast.error(errMsg(e)); } };
  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post("/v2/plans", { text: text.trim() }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!d) return null;
  const open = all ? d.open : d.open.slice(0, 3);
  return (
    <div className="card p-5" data-testid="plans-card">
      <span className="eyebrow flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" />{title}{d.open.length ? ` · ${d.open.length}` : ""}</span>
      {d.open.length === 0 && <p className="text-sm text-stone-600 mt-2" data-testid="plans-empty">Nothing on your list. Plans you add here or from a chat with CAMRYN show up here.</p>}
      <ul className="mt-1 divide-y divide-stone-100">
        {open.map((p) => <PlanRow key={p.id} p={p} onAct={act} today={d.today} onToday={() => { load(); onToday?.(); }} onPromote={onPromote} inToday={inToday} />)}
        {all && d.done.map((p) => <PlanRow key={p.id} p={p} onAct={act} />)}
      </ul>
      {(d.open.length > 3 || d.done.length > 0) && <button onClick={() => setAll(!all)} data-testid="plans-toggle-all" className="tap text-xs underline text-stone-600 mt-1">{all ? "Show less" : "Show all and recently done"}</button>}
      <form onSubmit={add} className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={120} placeholder="Add a plan" data-testid="plans-add-input"
          className="flex-1 min-w-0 h-11 px-4 rounded-full border border-stone-200 bg-white text-sm" />
        <button type="submit" aria-label="Add plan" data-testid="plans-add-btn" className="h-11 w-11 rounded-full bg-stone-900 text-white grid place-items-center"><Plus className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
